<template>
  <div id="app" :class="{
    'desktop-device': isDesktop,
    'auth-page': isAuthPage,
    'has-tabbar': hasTabbar
  }">
    <router-view></router-view>
    <Footer/>
    
    <!-- 桌面端提示 -->
    <div v-if="isDesktop" class="desktop-tip">
      <span>{{ $t('desktopMobilePage') }}</span>
    </div>
  </div>
</template>

<script>
import Footer from './common/Footer'
import { convertCmTextInElement } from './common/height'
export default {
  name: 'app',
  components: {
    Footer
  },
  data() {
    return {
      status: 0,
      isDesktop: false,
      desktopSwitchScheduled: false,
      heightTextObserver: null
    };
  },
  computed: {
    isAuthPage() {
      return this.$route.path === '/Login' || this.$route.path === '/Register';
    },
    hasTabbar() {
      return ['home', 'video', 'game', 'pl', 'mine'].includes(this.$route.name);
    }
  },
  watch: {
    '$route.fullPath'() {
      this.updateBodyRouteClass();
      this.$nextTick(() => this.syncDesktopPresentation());
    }
  },
  methods: {
    updateBodyRouteClass() {
      document.body.classList.toggle('auth-page-body', this.isAuthPage);
    },
    getBaseInfo(){
      this.$http({
        method: 'get',
        url: 'base_info'
      }).then(res=>{
        const isPublicAuthPage = this.$route.path === '/Login' || this.$route.path === '/Register'
        if(!localStorage.getItem('token') && !isPublicAuthPage){
          this.$router.push({path:'/Login'})
        }
        this.$store.commit('setBaseInfoValue', res.data);
      })
    },
    
    /**
     * 检测设备类型
     */
    detectDeviceType() {
      const userAgent = navigator.userAgent;
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      this.isDesktop = !isMobile && window.innerWidth > 768;
      
      // 添加到 body 类名，用于 CSS 控制
      if (this.isDesktop) {
        document.body.classList.add('desktop-device');
      } else {
        document.body.classList.remove('desktop-device');
      }
    },
    
    /**
     * 处理窗口大小变化
     */
    handleResize() {
      this.detectDeviceType();
    },
    syncDesktopPresentation() {
      const isEmbedded = new URLSearchParams(window.location.search).get('__desktop_frame') === '1';
      if (isEmbedded && window.parent !== window) {
        window.parent.postMessage({
          type: 'desktop-frame-route',
          hash: window.location.hash,
          title: document.title
        }, window.location.origin);
        return;
      }

      if (
        this.isDesktop
        && !this.isAuthPage
        && localStorage.getItem('token')
        && !this.desktopSwitchScheduled
      ) {
        this.desktopSwitchScheduled = true;
        window.setTimeout(() => window.location.reload(), 0);
      }
    }
  },
  created(){
    this.getBaseInfo();
  },
  mounted() {
    // 初始设备检测
    this.detectDeviceType();
    this.updateBodyRouteClass();
    this.syncDesktopPresentation();
    
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize);

    // Database descriptions may contain values such as "170cm" as part of a
    // complete text string. Convert those text nodes after every render/update.
    this.$nextTick(() => convertCmTextInElement(this.$el));
    this.heightTextObserver = new MutationObserver(() => {
      convertCmTextInElement(this.$el);
    });
    this.heightTextObserver.observe(this.$el, {
      childList: true,
      subtree: true,
      characterData: true
    });
  },
  beforeDestroy() {
    // 清理事件监听
    window.removeEventListener('resize', this.handleResize);
    if (this.heightTextObserver) {
      this.heightTextObserver.disconnect();
      this.heightTextObserver = null;
    }
    document.body.classList.remove('auth-page-body');
  }
}
</script>

<style>
/* 基础样式 */
body .van-toast {
  font-size: 1.1rem;
  padding: 1rem;
  line-height: 1.3rem;
  width: 10rem;
}
body .van-toast .van-toast__icon {
  font-size: 1.3rem;
}
*, :after, :before {
  box-sizing: border-box;
}

/* ===== 电脑端样式 - 强制显示为手机样式 ===== */
body.desktop-device {
  background: url('./assets/img/luxury-dating-bg-v2.png') center 30% / cover fixed no-repeat !important;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 1.25rem 0;
  margin: 0;
  overflow-x: hidden;
}

body.desktop-device #app {
  width: 26.875rem;
  min-height: 58.25rem;
  background: #050608;
  border-radius: 1.25rem;
  overflow: hidden;
  box-shadow: 
    0 1.25rem 2.5rem rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  position: relative;
}

/* Desktop authenticated pages run in a true phone-sized browsing context.
   This makes vw units and max-width media queries use the phone canvas rather
   than the full monitor width, preventing the desktop-only scale explosion. */
body.desktop-frame-host {
  min-height: 100vh;
  align-items: center;
  padding: 1.25rem;
}

body.desktop-frame-host > #app {
  display: none !important;
}

body.desktop-frame-host .desktop-mobile-frame {
  display: block;
  width: min(24.375rem, calc(100vw - 2rem));
  height: min(52.75rem, calc(100vh - 2.5rem));
  flex: 0 0 auto;
  border: .4rem solid rgba(255, 255, 255, .1);
  border-radius: 1.5rem;
  background: #050608;
  box-shadow: 0 1.5rem 4rem rgba(0, 0, 0, .58);
  overflow: hidden;
}

/* 登录/注册页使用完整背景，不套用手机外壳 */
body.desktop-device #app.auth-page {
  width: 100%;
  max-width: none;
  min-height: 100vh;
  border-radius: 0;
  border: 0 !important;
  background: transparent;
  box-shadow: none;
  overflow: hidden;
}

body.desktop-device.auth-page-body {
  padding: 0;
}

body.desktop-device #app.auth-page .desktop-tip {
  display: none;
}

/* 超大屏幕优化 */
@media (min-width: 1440px) {
  body.desktop-device {
    padding: 2.5rem 0;
  }
  
  body.desktop-device #app {
    border: .5rem solid rgba(255, 255, 255, 0.1);
  }
}

/* ===== 移动端样式 ===== */
body:not(.desktop-device) #app {
  width: 100%;
  max-width: 100%;
  background: #050608;
}

/* ===== 桌面端提示样式 ===== */
.desktop-tip {
  position: fixed;
  top: 1.25rem;
  right: 1.25rem;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: .5rem .75rem;
  border-radius: .375rem;
  font-size: .75rem;
  z-index: 10000;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ===== 确保页面内容在电脑端正确显示 ===== */
body.desktop-device .index,
body.desktop-device .home-page,
body.desktop-device .page-container {
  width: 100% !important;
  max-width: 100% !important;
  border-radius: 0 !important;
}

/* 修复导航栏在电脑端的圆角 */
body.desktop-device .van-nav-bar {
  border-radius: 1.25rem 1.25rem 0 0 !important;
}

/* 修复轮播图在电脑端的显示 */
body.desktop-device .banner.banners {
  border-radius: 0 !important;
}

/* 修复底部导航在电脑端的显示 */
body.desktop-device .footer-nav {
  border-radius: 0 0 1.25rem 1.25rem !important;
}

/* ===== 响应式优化 ===== */
@media (max-width: 430px) {
  body.desktop-device #app {
    width: 100%;
    border-radius: 0;
    margin: 0;
  }
}

/* 小屏幕手机优化 */
@media (max-width: 375px) {
  body:not(.desktop-device) #app {
    width: 100%;
  }
}

/* 超小屏幕优化 */
@media (max-width: 320px) {
  body:not(.desktop-device) #app {
    width: 100%;
  }
}

/* 无障碍访问支持 */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  
  .desktop-tip {
    animation: none;
  }
}

/* ===== 非登录页统一黑金轻奢主题 ===== */
body:not(.auth-page-body) {
  color: #f4ead7;
  background-color: #050608;
}

#app:not(.auth-page) {
  --luxury-gold: #dfbd75;
  --luxury-gold-strong: #f1d38e;
  --luxury-text: #f5eddf;
  --luxury-muted: #b9ad99;
  --luxury-panel: rgba(7, 10, 14, 0.94);
  --luxury-card: rgba(14, 17, 22, 0.92);
  --luxury-line: rgba(214, 177, 100, 0.42);
  color: var(--luxury-text);
  background:
    linear-gradient(rgba(3, 5, 8, 0.8), rgba(3, 5, 8, 0.94)),
    url('./assets/img/luxury-dating-bg-v2.png') center top / cover fixed no-repeat;
}

#app:not(.auth-page) .van-nav-bar {
  height: 56px !important;
  background: rgba(4, 6, 9, 0.96) !important;
  border-bottom: 1px solid var(--luxury-line) !important;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.35) !important;
}

#app:not(.auth-page) .van-nav-bar::after,
#app:not(.auth-page) .van-hairline--bottom::after {
  border-bottom-color: var(--luxury-line) !important;
}

#app:not(.auth-page) .van-nav-bar__title,
#app:not(.auth-page) .van-nav-bar__text,
#app:not(.auth-page) .van-nav-bar .van-icon {
  color: var(--luxury-gold-strong) !important;
}

#app:not(.auth-page) .van-nav-bar__title {
  max-width: 70% !important;
  font-size: 21px !important;
  font-weight: 700 !important;
  letter-spacing: 1px;
}

#app:not(.auth-page) .van-tabs__nav,
#app:not(.auth-page) .van-tabs__wrap,
#app:not(.auth-page) .van-tabs__content {
  background: rgba(8, 10, 14, 0.96) !important;
}

#app:not(.auth-page) .van-tab {
  color: #bdb3a2 !important;
  font-size: 17px !important;
}

#app:not(.auth-page) .van-tab--active {
  color: var(--luxury-gold-strong) !important;
  font-weight: 700 !important;
}

#app:not(.auth-page) .van-tabs__line {
  background: linear-gradient(90deg, #a77831, #efd58f) !important;
}

#app:not(.auth-page) .van-cell,
#app:not(.auth-page) .van-cell-group,
#app:not(.auth-page) .van-panel,
#app:not(.auth-page) .van-grid-item__content,
#app:not(.auth-page) .van-collapse-item__content,
#app:not(.auth-page) .van-collapse-item__title {
  color: var(--luxury-text) !important;
  background: var(--luxury-card) !important;
}

#app:not(.auth-page) .van-cell {
  min-height: 52px;
  font-size: 16px !important;
  border-bottom-color: rgba(214, 177, 100, 0.16) !important;
}

#app:not(.auth-page) .van-cell__title,
#app:not(.auth-page) .van-cell__value,
#app:not(.auth-page) .van-field__label {
  color: var(--luxury-text) !important;
}

#app:not(.auth-page) .van-field__control,
#app:not(.auth-page) .van-field__control::placeholder {
  color: #d6cdbc !important;
  font-size: 16px !important;
}

#app:not(.auth-page) .van-popup,
#app:not(.auth-page) .van-dialog,
#app:not(.auth-page) .van-action-sheet,
#app:not(.auth-page) .van-picker,
#app:not(.auth-page) .van-picker__columns {
  color: var(--luxury-text) !important;
  background: #090c11 !important;
}

#app:not(.auth-page) .van-dialog,
#app:not(.auth-page) .van-popup--round,
#app:not(.auth-page) .van-action-sheet {
  border: 1px solid var(--luxury-line) !important;
  border-radius: 22px !important;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.55) !important;
}

#app:not(.auth-page) .van-dialog__header,
#app:not(.auth-page) .van-action-sheet__header,
#app:not(.auth-page) .van-picker__title {
  color: var(--luxury-gold-strong) !important;
  font-size: 19px !important;
  font-weight: 700 !important;
}

#app:not(.auth-page) .van-button--primary,
#app:not(.auth-page) .van-button--info,
#app:not(.auth-page) .van-dialog__confirm,
#app:not(.auth-page) button[type='submit'] {
  color: #171006 !important;
  font-size: 17px !important;
  font-weight: 700 !important;
  background: linear-gradient(110deg, #ad7c34, #ecd085, #bf8a3f) !important;
  border-color: rgba(245, 215, 145, 0.72) !important;
  box-shadow: 0 8px 22px rgba(185, 130, 48, 0.24) !important;
}

#app:not(.auth-page) .van-tag {
  color: #ead09a !important;
  font-size: 13px !important;
  background: rgba(190, 139, 56, 0.12) !important;
  border: 1px solid rgba(222, 184, 105, 0.5) !important;
}

#app:not(.auth-page) .van-empty__description,
#app:not(.auth-page) .van-list__finished-text,
#app:not(.auth-page) .van-list__loading,
#app:not(.auth-page) .van-loading__text {
  color: var(--luxury-muted) !important;
  font-size: 15px !important;
}

#app:not(.auth-page) h1,
#app:not(.auth-page) h2,
#app:not(.auth-page) h3,
#app:not(.auth-page) .section-title,
#app:not(.auth-page) .title {
  color: var(--luxury-gold-strong) !important;
}

#app:not(.auth-page) h2,
#app:not(.auth-page) .section-title {
  font-size: 20px !important;
}

#app:not(.auth-page) .van-overlay {
  background-color: rgba(0, 0, 0, 0.76) !important;
  backdrop-filter: blur(3px);
}

#app:not(.auth-page) .van-toast {
  color: #fff4d7 !important;
  background: rgba(8, 10, 14, 0.94) !important;
  border: 1px solid var(--luxury-line) !important;
}

@media (max-width: 430px) {
  #app:not(.auth-page) .van-nav-bar__title {
    font-size: 20px !important;
  }

  #app:not(.auth-page) .van-tab,
  #app:not(.auth-page) .van-cell,
  #app:not(.auth-page) .van-field__control {
    font-size: 16px !important;
  }
}

/* 绑定提现账户页 */
#app:not(.auth-page) .bind-page {
  min-height: 100vh;
  color: #f3eadb;
  background: linear-gradient(rgba(3,5,8,.82),rgba(3,5,8,.97)), url('./assets/img/luxury-dating-bg-v2.png') center top / cover fixed no-repeat !important;
}

#app:not(.auth-page) .bind-page .nav-header {
  height: 4.5rem !important;
  padding: 0 1rem !important;
  display: grid !important;
  grid-template-columns: 4rem 1fr 4rem;
  align-items: center;
  border-bottom: 1px solid rgba(218,180,105,.42) !important;
  background: rgba(4,6,9,.97) !important;
  box-shadow: 0 .6rem 1.6rem rgba(0,0,0,.4) !important;
}

#app:not(.auth-page) .bind-page .nav-back {
  width: 3.5rem;
  height: 100%;
  display: flex;
  align-items: center;
  color: #efd18a !important;
  font-size: 1.65rem !important;
}

#app:not(.auth-page) .bind-page .nav-title {
  color: #efd18a !important;
  font-size: 1.15rem !important;
  font-weight: 750 !important;
  text-align: center;
}

#app:not(.auth-page) .bind-page .form-content {
  padding: 1rem .8rem 2rem !important;
}

#app:not(.auth-page) .bind-page .input-card {
  margin-bottom: .85rem !important;
  padding: 1rem !important;
  border: 1px solid rgba(218,180,105,.32) !important;
  border-radius: 1.15rem !important;
  color: #f2e8d8 !important;
  background: linear-gradient(145deg,rgba(15,18,23,.96),rgba(6,8,12,.98)) !important;
  box-shadow: 0 .85rem 2rem rgba(0,0,0,.38), inset 0 1px 0 rgba(255,255,255,.035) !important;
}

#app:not(.auth-page) .bind-page .input-label {
  margin-bottom: .75rem !important;
  color: #e5c47d !important;
  font-size: .95rem !important;
  font-weight: 700 !important;
}

#app:not(.auth-page) .bind-page .bank-display,
#app:not(.auth-page) .bind-page .input-field {
  min-height: 3.3rem;
  border: 1px solid rgba(218,180,105,.25) !important;
  border-radius: .85rem !important;
  background: rgba(3,5,8,.62) !important;
}

#app:not(.auth-page) .bind-page .bank-text {
  color: #ded2be !important;
  font-size: .9rem !important;
}

#app:not(.auth-page) .bind-page .bank-arrow {
  color: #d8b66f !important;
}

#app:not(.auth-page) .bind-page .van-field__control {
  min-height: 2.75rem;
  color: #f3eadb !important;
  font-size: .9rem !important;
}

#app:not(.auth-page) .bind-page .van-field__control::placeholder {
  color: #766f64 !important;
}

#app:not(.auth-page) .bind-page .submit-btn {
  width: 100%;
  min-height: 3.5rem !important;
  margin-top: .8rem !important;
  color: #171006 !important;
  font-size: 1.05rem !important;
  font-weight: 750 !important;
  border: 1px solid rgba(247,220,160,.68) !important;
  border-radius: 1rem !important;
  background: linear-gradient(110deg,#a97732,#e7c87c,#bb853c) !important;
  box-shadow: 0 .75rem 1.8rem rgba(185,130,48,.3) !important;
}

#app:not(.auth-page) .bank-action-sheet,
#app:not(.auth-page) .bank-action-sheet .van-action-sheet__header {
  color: #f3eadb !important;
  background: #090c11 !important;
}

#app:not(.auth-page) .bank-action-sheet .van-action-sheet__header {
  color: #efd18a !important;
}

#app:not(.auth-page) .bank-action-sheet .bank-item {
  border-color: rgba(218,180,105,.22) !important;
  background: rgba(216,177,99,.05) !important;
}

#app:not(.auth-page) .bank-action-sheet .bank-name {
  color: #ead09a !important;
}

/* 全局提示、确认框与弹窗挂载在 body 下，需要独立覆盖 */
body .van-toast {
  min-width: 8.5rem !important;
  max-width: 82vw !important;
  min-height: 3.5rem !important;
  padding: .85rem 1.2rem !important;
  color: #f5ead6 !important;
  font-size: .95rem !important;
  line-height: 1.45 !important;
  border: 1px solid rgba(226,190,116,.55) !important;
  border-radius: 1rem !important;
  background: linear-gradient(145deg,rgba(17,19,23,.97),rgba(5,7,10,.98)) !important;
  box-shadow: 0 1rem 2.8rem rgba(0,0,0,.58), inset 0 1px 0 rgba(255,255,255,.055) !important;
  backdrop-filter: blur(16px) saturate(125%);
}

body .van-toast__text {
  color: #f1d38e !important;
  font-size: .95rem !important;
  font-weight: 650;
  letter-spacing: .02em;
}

body .van-toast .van-toast__icon {
  color: #e7c77f !important;
  font-size: 1.75rem !important;
  margin-bottom: .4rem;
}

body .van-toast--text {
  min-width: 9rem !important;
  min-height: auto !important;
  padding: .9rem 1.3rem !important;
}

body .van-overlay {
  background: rgba(0,0,0,.76) !important;
  backdrop-filter: blur(4px);
}

body .van-dialog {
  overflow: hidden;
  color: #f3eadb !important;
  border: 1px solid rgba(226,190,116,.48) !important;
  border-radius: 1.3rem !important;
  background: linear-gradient(145deg,#111419,#07090d) !important;
  box-shadow: 0 1.4rem 3.5rem rgba(0,0,0,.62) !important;
}

body .van-dialog__header {
  padding-top: 1.25rem !important;
  color: #efd18a !important;
  font-size: 1.08rem !important;
  font-weight: 750 !important;
  border-bottom: 1px solid rgba(218,180,105,.22) !important;
  background: linear-gradient(110deg,rgba(31,24,13,.98),rgba(7,10,14,.98)) !important;
}

body .van-dialog__content,
body .van-dialog__message {
  color: #cfc3af !important;
  background: transparent !important;
}

body .van-dialog .van-cell,
body .van-dialog .van-field {
  color: #f3eadb !important;
  background: rgba(4,6,9,.72) !important;
}

body .van-dialog .van-field__label {
  color: #d8c7aa !important;
  font-size: .88rem !important;
}

body .van-dialog .van-field__control {
  color: #f3eadb !important;
  font-size: .9rem !important;
}

body .van-dialog .van-field__control::placeholder {
  color: #776f63 !important;
}

body .van-dialog__footer {
  border-top: 1px solid rgba(218,180,105,.2) !important;
  background: rgba(3,5,8,.5) !important;
}

body .van-dialog__cancel,
body .van-dialog__confirm {
  min-height: 3.3rem !important;
  font-size: .95rem !important;
  border-color: rgba(218,180,105,.18) !important;
  background: transparent !important;
}

body .van-dialog__cancel {
  color: #aaa08f !important;
  background: #090c11 !important;
}

body .van-dialog__confirm {
  color: #171006 !important;
  font-weight: 700;
  background: linear-gradient(110deg,#a97732,#e7c87c,#bb853c) !important;
}

/* Shared responsive safety net for every route. Component styles can still
   choose their own layout, but no child may force the viewport wider. */
html,
body,
#app {
  width: 100%;
  min-width: 0;
  min-height: 100%;
  overflow-x: hidden;
}

#app img,
#app video,
#app canvas,
#app svg {
  max-width: 100%;
}

#app input,
#app textarea,
#app select,
#app button {
  max-width: 100%;
}

#app .van-row,
#app .van-col,
#app .van-cell,
#app .van-cell__title,
#app .van-cell__value,
#app .van-field,
#app .van-field__body,
#app .van-field__control {
  min-width: 0;
}

#app.has-tabbar {
  padding-bottom: calc(3.5rem + env(safe-area-inset-bottom));
}

#app .van-cell__title,
#app .van-cell__value,
#app .van-nav-bar__title,
#app .van-tab,
#app .van-button__text {
  overflow-wrap: anywhere;
}

@media (max-width: 430px) {
  :root {
    font-size: clamp(87.5%, 4.1vw, 100%);
  }

  #app:not(.auth-page) .van-nav-bar {
    height: clamp(3rem, 14vw, 3.5rem) !important;
  }

  #app:not(.auth-page) .van-nav-bar__title {
    width: calc(100% - 6.5rem);
    max-width: calc(100% - 6.5rem) !important;
    font-size: clamp(1.0625rem, 5vw, 1.25rem) !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  #app:not(.auth-page) .van-dialog,
  body .van-dialog {
    width: calc(100vw - 1.5rem) !important;
    max-width: 26rem;
  }

  #app .van-button,
  #app button {
    overflow-wrap: anywhere;
  }

  #app:not(.auth-page) .van-button:not(.van-button--mini),
  #app:not(.auth-page) .van-button:not(.van-button--mini) .van-button__text {
    font-size: 16px !important;
    line-height: 1.35;
  }

  #app:not(.auth-page) .van-empty__description,
  #app:not(.auth-page) .van-list__finished-text,
  #app:not(.auth-page) .van-list__loading,
  #app:not(.auth-page) .van-loading__text {
    font-size: 14px !important;
    line-height: 1.4;
  }

  #app:not(.auth-page) .van-nav-bar__text,
  #app:not(.auth-page) .van-cell-group__title {
    font-size: 14px !important;
  }

  #app:not(.auth-page) .van-cell__title,
  #app:not(.auth-page) .van-cell__value,
  #app:not(.auth-page) .van-cell__label,
  #app:not(.auth-page) .van-field__label {
    font-size: 14px !important;
  }

  #app:not(.auth-page) .bind-page .van-field__control,
  #app:not(.auth-page) .bind-page .van-field__control::placeholder {
    font-size: 16px !important;
  }

  #app:not(.auth-page) h2,
  #app:not(.auth-page) .section-title {
    font-size: 20px !important;
    line-height: 1.35;
  }

  #app .van-field__control,
  #app .van-field__control::placeholder,
  #app input,
  #app input::placeholder,
  #app textarea,
  #app textarea::placeholder,
  #app select {
    font-size: max(1rem, 12pt) !important;
  }

  #app .van-button:not(.van-button--mini) {
    min-height: 2.75rem;
  }
}

@media (max-width: 340px) {
  :root {
    font-size: 87.5%;
  }

  #app:not(.auth-page) .van-cell {
    padding-right: .75rem;
    padding-left: .75rem;
  }
}

body .van-action-sheet,
body .van-popup {
  color: #f3eadb !important;
  border-color: rgba(226,190,116,.45) !important;
  background: #090c11 !important;
}

body .van-action-sheet {
  border: 1px solid rgba(226,190,116,.45) !important;
  border-bottom: 0 !important;
  border-radius: 1.4rem 1.4rem 0 0 !important;
  box-shadow: 0 -1.2rem 3rem rgba(0,0,0,.55) !important;
}

body .van-action-sheet__header {
  color: #efd18a !important;
  font-size: 1.05rem !important;
  font-weight: 750 !important;
  border-bottom: 1px solid rgba(218,180,105,.2);
}

body .van-action-sheet__item,
body .van-action-sheet__cancel {
  color: #ddd1bd !important;
  background: #0b0e13 !important;
}

body .van-notify {
  color: #171006 !important;
  font-size: .9rem !important;
  font-weight: 700;
  background: linear-gradient(110deg,#ad7c34,#ecd085,#bf8a3f) !important;
  box-shadow: 0 .6rem 1.5rem rgba(0,0,0,.35);
}
</style>
